/**
 * Leitor de Markdown — subconjunto pequeno, sem dependência e sem HTML.
 *
 * **Devolve estrutura, não texto de HTML.** Não existe `innerHTML` no caminho,
 * então não existe caminho de injeção: quem renderiza monta elementos React a
 * partir destes nós, e o que não é reconhecido vira texto literal.
 *
 * **Por que um leitor próprio, e não uma biblioteca.** O caderno da F1 precisa
 * de títulos, listas, ênfase, código e link — nada além. Uma biblioteca de
 * Markdown completa pesa mais que tudo isso junto, e a F2 é uma PWA que vai
 * medir cada quilobyte. A decisão do **editor de blocos** continua adiada,
 * como combinado; quando ela vier, o renderizador dela substitui este.
 *
 * **Ele não é mais o leitor do caderno.** A RN-39 trocou a fonte da verdade da
 * nota para HTML, e quem exibe passou a ser `conteudo.ts`. O que sobrou aqui é
 * a direção Markdown → estrutura, usada por `htmlDeMarkdown` para converter
 * texto escrito em Markdown — o esboço da dúvida, e a migração de notas antigas
 * se algum dia houver alguma.
 */
/**
 * Só `http` e `https`.
 *
 * `javascript:` num link é o vetor clássico, e vale barrar mesmo num app de um
 * usuário só: o Markdown pode vir colado de qualquer lugar, e o custo de barrar
 * é uma linha.
 */
function hrefSeguro(url) {
    const limpo = url.trim();
    return /^https?:\/\//i.test(limpo) ? limpo : null;
}
/** Reconhece `**forte**`, `*ênfase*`, `` `código` `` e `[texto](url)`. */
export function lerTrechos(linha) {
    const trechos = [];
    let buffer = "";
    const despejar = () => {
        if (buffer !== "") {
            trechos.push({ tipo: "texto", valor: buffer });
            buffer = "";
        }
    };
    let i = 0;
    while (i < linha.length) {
        const resto = linha.slice(i);
        // Código antes de tudo: dentro de crase nada mais é interpretado.
        const codigo = /^`([^`]+)`/.exec(resto);
        if (codigo) {
            despejar();
            trechos.push({ tipo: "codigo", valor: codigo[1] });
            i += codigo[0].length;
            continue;
        }
        const forte = /^\*\*([^*]+)\*\*/.exec(resto);
        if (forte) {
            despejar();
            trechos.push({ tipo: "forte", valor: forte[1] });
            i += forte[0].length;
            continue;
        }
        const enfase = /^\*([^*]+)\*/.exec(resto);
        if (enfase) {
            despejar();
            trechos.push({ tipo: "enfase", valor: enfase[1] });
            i += enfase[0].length;
            continue;
        }
        const link = /^\[([^\]]*)\]\(([^)\s]+)\)/.exec(resto);
        if (link) {
            const href = hrefSeguro(link[2]);
            despejar();
            if (href) {
                trechos.push({ tipo: "link", texto: link[1] || href, href });
            }
            else {
                // Esquema não permitido: vira texto literal, incluindo os colchetes.
                // Sumir com o link seria pior — o usuário perderia o que escreveu.
                trechos.push({ tipo: "texto", valor: link[0] });
            }
            i += link[0].length;
            continue;
        }
        buffer += linha[i];
        i++;
    }
    despejar();
    return juntarTexto(trechos);
}
/**
 * Funde trechos de texto vizinhos.
 *
 * Eles aparecem quando algo quase virou marcação e não virou — um link com
 * esquema recusado, por exemplo, que sai como texto e deixa o resto do
 * parêntese em outro pedaço. Fundir mantém a saída canônica: o mesmo texto
 * sempre produz a mesma árvore, o que é o que torna o teste confiável.
 */
function juntarTexto(trechos) {
    const saida = [];
    for (const t of trechos) {
        const ultimo = saida[saida.length - 1];
        if (t.tipo === "texto" && ultimo?.tipo === "texto") {
            ultimo.valor += t.valor;
            continue;
        }
        saida.push(t);
    }
    return saida;
}
/** Converte o Markdown em blocos. Puro: mesma entrada, mesma saída, sempre. */
export function lerMarkdown(texto) {
    const linhas = texto.replace(/\r\n/g, "\n").split("\n");
    const blocos = [];
    let paragrafo = [];
    const fecharParagrafo = () => {
        if (paragrafo.length === 0)
            return;
        blocos.push({ tipo: "paragrafo", trechos: lerTrechos(paragrafo.join(" ")) });
        paragrafo = [];
    };
    let i = 0;
    while (i < linhas.length) {
        const linha = linhas[i];
        // --- bloco de código: tudo dentro sai literal ---
        const cerca = /^```\s*(\S*)\s*$/.exec(linha);
        if (cerca) {
            fecharParagrafo();
            const dentro = [];
            i++;
            while (i < linhas.length && !/^```\s*$/.test(linhas[i])) {
                dentro.push(linhas[i]);
                i++;
            }
            i++; // a cerca de fechamento
            blocos.push({ tipo: "codigo", texto: dentro.join("\n"), linguagem: cerca[1] || null });
            continue;
        }
        if (linha.trim() === "") {
            fecharParagrafo();
            i++;
            continue;
        }
        if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(linha.trim())) {
            fecharParagrafo();
            blocos.push({ tipo: "regua" });
            i++;
            continue;
        }
        const titulo = /^(#{1,3})\s+(.*)$/.exec(linha);
        if (titulo) {
            fecharParagrafo();
            blocos.push({
                tipo: "titulo",
                nivel: titulo[1].length,
                trechos: lerTrechos(titulo[2].trim()),
            });
            i++;
            continue;
        }
        const citacao = /^>\s?(.*)$/.exec(linha);
        if (citacao) {
            fecharParagrafo();
            const dentro = [citacao[1]];
            i++;
            while (i < linhas.length && /^>\s?/.test(linhas[i])) {
                dentro.push(linhas[i].replace(/^>\s?/, ""));
                i++;
            }
            blocos.push({ tipo: "citacao", trechos: lerTrechos(dentro.join(" ")) });
            continue;
        }
        const marcador = /^\s*([-*+]|\d+\.)\s+(.*)$/.exec(linha);
        if (marcador) {
            fecharParagrafo();
            const ordenada = /\d/.test(marcador[1]);
            const itens = [];
            while (i < linhas.length) {
                const m = /^\s*([-*+]|\d+\.)\s+(.*)$/.exec(linhas[i]);
                if (!m)
                    break;
                // Trocar de marcador começa outra lista: `-` e `1.` são coisas
                // diferentes, e juntá-las inventaria numeração que ninguém escreveu.
                if (/\d/.test(m[1]) !== ordenada)
                    break;
                itens.push(lerTrechos(m[2]));
                i++;
            }
            blocos.push({ tipo: "lista", ordenada, itens });
            continue;
        }
        paragrafo.push(linha.trim());
        i++;
    }
    fecharParagrafo();
    return blocos;
}
//# sourceMappingURL=markdown.js.map